import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useCallback, useMemo } from 'react';
import { TimeFormats } from 'src/graphql';
import { useTimeFormat } from 'src/graphql/entities/time-format/use-time-format';
import { isDefault12HourFormat } from 'src/utils/date';
import { useViewerTimezone } from './use-viewer-timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const TIMESTAMP_WITH_TIMEZONE_PATTERN = /(?:[T\s].*)([Zz]|[+-]\d{2}(?::?\d{2})?)$/;

export const useDisplayTime = (options?: { showTimezone?: boolean; tz?: string }) => {
  const { showTimezone = false, tz } = options || {};
  const { currentTimeFormat } = useTimeFormat();
  const viewerTimezoneName = useViewerTimezone();
  const targetTimezone = tz ?? viewerTimezoneName;

  const { timeFormatPref, timeFormat } = useMemo(() => {
    const tzSuffix = showTimezone ? ' [GMT]Z' : '';
    if (
      currentTimeFormat === TimeFormats.TwelveHour ||
      (currentTimeFormat === TimeFormats.Auto && isDefault12HourFormat())
    ) {
      return { timeFormatPref: TimeFormats.TwelveHour, timeFormat: `h:mm A${tzSuffix}` };
    }
    return { timeFormatPref: TimeFormats.TwentyFourHour, timeFormat: `HH:mm${tzSuffix}` };
  }, [currentTimeFormat, showTimezone]);

  const displayTime = useCallback(
    (input: string | Date | dayjs.Dayjs) => {
      // decide whether to CONVERT or PARSE
      const isString = typeof input === 'string';
      const str = isString ? input : '';
      const hasOffset = isString && TIMESTAMP_WITH_TIMEZONE_PATTERN.test(str);

      if (targetTimezone) {
        // if input carries an offset/UTC or is Date/Dayjs - convert
        if (hasOffset || input instanceof Date || dayjs.isDayjs(input)) {
          return dayjs(input).tz(targetTimezone).format(timeFormat);
        }
        // naive string from database is UTC - parse as UTC, then convert to target timezone
        return dayjs.utc(str).tz(targetTimezone).format(timeFormat);
      }

      // no target tz: format as-is (local)
      return dayjs(input).format(timeFormat);
    },
    [targetTimezone, timeFormat],
  );

  return useMemo(
    () => ({ timeFormat, timeFormatPref, displayTime }),
    [timeFormat, timeFormatPref, displayTime],
  );
};
